import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap"
import { useSelector } from "react-redux"

import Api from "@/api"
import routes from "@/routes"
import ErrorBoundary from "@/components/ErrorBoundary"
import { getProduct, getProductId } from "@/onboarding/features/product/selectors"
import { getImportSections } from "@/onboarding/features/product/components/header/importSections"

import {
  buildRows,
  isComponentFieldSet,
  groupCleanRows,
  reconcileSelection,
  summarizeReport,
  TargetRow,
} from "./helpers"

const SEARCH_DEBOUNCE_MS = 300
const RUN_POLL_INTERVAL_MS = 2000
// ApplyToTours::Diff::TARGET_CAP — diff requests over larger id sets go out
// in batches of this size.
// Batch size trades request count against wall-clock parallelism: the diff
// endpoint caps a request at ApplyToTours::Diff::TARGET_CAP (50), but the
// batches run through Promise.all, so smaller batches fan the classification
// out across server workers instead of serialising it in one request.
const DIFF_BATCH_SIZE = 10

const OUTCOME_LABELS = {
  applied: "Applied",
  unchanged: "Already up to date",
  no_editable_bid: "No editable bid",
  locked_by_cruise_line: "Locked by cruise line",
  failed: "Failed",
  pending: "Pending",
}

const STATE_BADGES: Record<TargetRow["state"], { label: string; className: string }> = {
  clean: { label: "No conflicts", className: "text-success" },
  overwrite: { label: "Will overwrite", className: "text-danger" },
  locked: { label: "Locked by cruise line", className: "text-muted" },
  unavailable: { label: "No editable bid", className: "text-muted" },
}

const OUTCOME_CLASSES = {
  applied: "text-success",
  unchanged: "text-muted",
  no_editable_bid: "text-warning",
  locked_by_cruise_line: "text-warning",
  failed: "text-danger",
  pending: "text-muted",
}

const RunReport = ({ report, labelsById }) => {
  const summary = summarizeReport(report)

  return (
    <div className="d-flex flex-column gap-2">
      <div>
        {Object.entries(summary).map(([outcome, count]) => (
          <span key={outcome} className="me-3">
            <b>{OUTCOME_LABELS[outcome] ?? outcome}:</b> {count}
          </span>
        ))}
      </div>
      <Table size="sm" className="mb-0">
        <tbody>
          {report.map((entry) => (
            <tr key={entry.productId}>
              <td className="border-0">
                <a
                  href={routes.onboarding_product_path({ id: entry.productId })}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary text-decoration-underline"
                >
                  {labelsById[entry.productId] ?? entry.productId}
                  <i className="fas fa-external-link-alt ms-1 small" />
                </a>
              </td>
              <td className="border-0 text-end">
                <span className={OUTCOME_CLASSES[entry.outcome] ?? ""}>
                  {OUTCOME_LABELS[entry.outcome] ?? entry.outcome}
                  {entry.message && ` — ${entry.message}`}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const RowCheckbox = ({ row, checked, onToggle }) => (
  <tr>
    <td className="border-0" style={{ width: "2rem" }}>
      <Form.Check
        type="checkbox"
        id={`apply-to-tours-${row.productId}`}
        checked={checked}
        disabled={!row.selectable}
        onChange={onToggle}
      />
    </td>
    <td className="border-0">{row.label}</td>
    <td className="border-0 text-end">
      <span className={STATE_BADGES[row.state].className}>
        {STATE_BADGES[row.state].label}
        {row.state === "overwrite" &&
          ` (${row.changedFieldCount} field${row.changedFieldCount === 1 ? "" : "s"})`}
      </span>
    </td>
  </tr>
)

const ApplyToToursModal: FC<{
  show: boolean
  onHide: () => void
  initialSectionName?: string
}> = ({ show, onHide, initialSectionName }) => {
  const productId = useSelector(getProductId)
  const { port } = useSelector(getProduct)
  // The same section registry as "Import from Tour" — apply is its push twin.
  const sections = useSelector(getImportSections)

  const [selectedSections, setSelectedSections] = useState<Record<string, boolean>>(
    initialSectionName ? { [initialSectionName]: true } : {}
  )
  const fieldNames = useMemo(
    () =>
      Array.from(
        new Set(
          sections
            .filter((section) => selectedSections[section.name])
            .flatMap((section) => section.fieldNames)
        )
      ),
    [sections, selectedSections]
  )
  // Effect-dependency key: fieldNames is a fresh array every render.
  const fieldNamesKey = fieldNames.join(",")
  const componentFieldSet = isComponentFieldSet(fieldNames)

  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<TargetRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  // Selection is sticky across searches: the operator can filter, tick,
  // refilter and keep building one selection. Selected rows stay visible in
  // their own pinned section even when the current search excludes them.
  const [selectedRows, setSelectedRows] = useState<Map<string, TargetRow>>(new Map())
  const [deselectedCount, setDeselectedCount] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [run, setRun] = useState(null)
  const [error, setError] = useState(null)
  const pollTimer = useRef(null)
  // The load effect reconciles the selection against fresh diffs without
  // depending on it (that would re-run the load on every tick).
  const selectedRowsRef = useRef(selectedRows)
  useEffect(() => {
    selectedRowsRef.current = selectedRows
  }, [selectedRows])
  const prevFieldNamesKey = useRef(fieldNamesKey)
  const prevComponentFieldSet = useRef(componentFieldSet)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!show) return undefined

    const fieldSetChanged = prevFieldNamesKey.current !== fieldNamesKey
    const scopeTightened = componentFieldSet && !prevComponentFieldSet.current
    prevFieldNamesKey.current = fieldNamesKey
    prevComponentFieldSet.current = componentFieldSet

    if (fieldNames.length === 0) {
      setRows([])
      setTotalCount(0)
      setLoading(false)
      return undefined
    }

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const portId = componentFieldSet ? port.id : undefined
        const {
          data: {
            data: { options, totalCount: total },
          },
        } = await Api.onboardingProduct.searchOperatorApplicableProducts(
          productId,
          debouncedQuery,
          portId
        )

        // On a section-set change the existing selection must be re-verified
        // too, so its ids join the diff — batched to the backend's cap.
        const idSet = new Set<string>(options.map((option) => option.value))
        if (fieldSetChanged) {
          selectedRowsRef.current.forEach((_row, id) => idSet.add(id))
        }
        const ids = Array.from(idSet)

        const diffsById = {}
        const batches = []
        for (let start = 0; start < ids.length; start += DIFF_BATCH_SIZE) {
          batches.push(ids.slice(start, start + DIFF_BATCH_SIZE))
        }
        await Promise.all(
          batches.map(async (batch) => {
            const {
              data: {
                data: { diffs },
              },
            } = await Api.onboardingProduct.applyToToursDiff(productId, fieldNames, batch)
            diffs.forEach((diff) => {
              diffsById[diff.productId] = diff
            })
          })
        )

        if (!cancelled) {
          const newRows = buildRows(options, diffsById, componentFieldSet)
          setRows(newRows)
          setTotalCount(total)
          if (fieldSetChanged) {
            const { next, droppedCount } = reconcileSelection(
              selectedRowsRef.current,
              newRows,
              diffsById,
              componentFieldSet,
              scopeTightened
            )
            setSelectedRows(next)
            setDeselectedCount(droppedCount)
          }
        }
      } catch {
        if (!cancelled) setError("Could not load the target tours. Please try again.")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [show, debouncedQuery, fieldNamesKey])

  // Poll an async run until it settles.
  useEffect(() => {
    if (!run || run.status === "completed" || run.status === "failed") return undefined

    pollTimer.current = setInterval(async () => {
      const {
        data: {
          data: { run: polled },
        },
      } = await Api.onboardingProduct.applyToToursRun(productId, run.id)
      setRun(polled)
    }, RUN_POLL_INTERVAL_MS)

    return () => clearInterval(pollTimer.current)
  }, [run?.status, run?.id])

  const allSectionsSelected =
    sections.length > 0 && sections.every((section) => selectedSections[section.name])

  const toggleSection = (name: string) => {
    setSelectedSections((current) => ({ ...current, [name]: !current[name] }))
  }

  const toggleAllSections = () => {
    setSelectedSections(
      Object.fromEntries(sections.map((section) => [section.name, !allSectionsSelected]))
    )
  }

  const toggleRow = (row: TargetRow) => {
    setSelectedRows((current) => {
      const next = new Map(current)
      if (next.has(row.productId)) {
        next.delete(row.productId)
      } else {
        next.set(row.productId, row)
      }
      return next
    })
  }

  // Selected rows stay in place in the results list (no jumping). Rows
  // selected under an earlier search that the current one excludes are the
  // only ones needing a separate home: a collapsed summary, expandable to
  // review and deselect.
  const hiddenSelectedRows = useMemo(() => {
    const visibleIds = new Set(rows.map((row) => row.productId))
    return Array.from(selectedRows.values()).filter((row) => !visibleIds.has(row.productId))
  }, [rows, selectedRows])
  const [showHiddenSelected, setShowHiddenSelected] = useState(false)

  const groupAllSelected = (group: string) => {
    const clean = groupCleanRows(rows, group)
    return clean.length > 0 && clean.every((row) => selectedRows.has(row.productId))
  }

  const toggleGroup = (group: string) => {
    const clean = groupCleanRows(rows, group)
    const deselecting = groupAllSelected(group)

    setSelectedRows((current) => {
      const next = new Map(current)
      clean.forEach((row) => {
        if (deselecting) {
          next.delete(row.productId)
        } else {
          next.set(row.productId, row)
        }
      })
      return next
    })
  }

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const {
        data: {
          data: { run: created },
        },
      } = await Api.onboardingProduct.applyToTours(
        productId,
        fieldNames,
        Array.from(selectedRows.keys())
      )
      setRun(created)
    } catch {
      setError("The apply could not be started. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Apply to Tour/s</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorBoundary>
          {error && <Alert variant="danger">{error}</Alert>}
          {run ? (
            <div className="d-flex flex-column gap-2">
              {run.status === "queued" || run.status === "running" ? (
                <div>
                  <Spinner size="sm" className="me-2" />
                  Applying to {run.report.length} tours…
                </div>
              ) : (
                <RunReport
                  report={run.report}
                  labelsById={Object.fromEntries(
                    Array.from(selectedRows.values()).map((row) => [row.productId, row.label])
                  )}
                />
              )}
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              <p className="mb-0">
                Copies the selected sections&apos; last saved values from this bid to the selected
                tours&apos; bids{" "}
                <b className="text-warning">(make sure to save your edits first)</b>. Tours marked{" "}
                <b className="text-danger">Will overwrite</b> already hold different values and must
                be selected individually.
              </p>
              <div className="border rounded p-2">
                <div className="d-flex justify-content-between align-items-center">
                  <b>Sections</b>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 border-0 align-baseline text-primary text-decoration-underline"
                    onClick={toggleAllSections}
                  >
                    {allSectionsSelected ? "Clear all" : "Select all sections"}
                  </Button>
                </div>
                <div className="d-flex flex-wrap">
                  {sections.map((section) => (
                    <div key={section.name} className="w-50">
                      <Form.Check
                        type="checkbox"
                        id={`apply-to-tours-section-${section.name}`}
                        label={section.title}
                        checked={!!selectedSections[section.name]}
                        onChange={() => toggleSection(section.name)}
                      />
                    </div>
                  ))}
                </div>
                {componentFieldSet && (
                  <div className="text-muted small">
                    Component sections limit the target tours to {port.name}.
                  </div>
                )}
              </div>
              {deselectedCount > 0 && (
                <Alert
                  variant="warning"
                  className="mb-0 py-1"
                  dismissible
                  onClose={() => setDeselectedCount(0)}
                >
                  {deselectedCount} selected tour{deselectedCount === 1 ? "" : "s"} deselected — the
                  new section selection would overwrite or block{" "}
                  {deselectedCount === 1 ? "it" : "them"}.
                </Alert>
              )}
              {fieldNames.length === 0 ? (
                <p className="mb-0">Select at least one section to continue.</p>
              ) : (
                <>
                  <Form.Control
                    type="search"
                    placeholder="Search by tour name, code or port"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      {totalCount > rows.length && (
                        <Alert variant="info" className="mb-0 py-1">
                          Showing the first {rows.length} of {totalCount} tours — refine the search
                          to reach the rest.
                        </Alert>
                      )}
                      {selectedRows.size > 0 && (
                        <div className="border rounded px-2 py-1">
                          <b>{selectedRows.size} selected</b>
                          {hiddenSelectedRows.length > 0 && (
                            <>
                              {" — "}
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 align-baseline"
                                onClick={() => setShowHiddenSelected((expanded) => !expanded)}
                              >
                                {hiddenSelectedRows.length} outside these results
                                {/* Both icons stay mounted, visibility via the
                                wrapper span (SectionHolder's pattern): Font
                                Awesome replaces each <i> with an <svg>, so
                                React updates to the <i> never reach the DOM. */}
                                <span className={showHiddenSelected ? "d-none" : ""}>
                                  <i className="fas fa-chevron-down ms-2" />
                                </span>
                                <span className={showHiddenSelected ? "" : "d-none"}>
                                  <i className="fas fa-chevron-up ms-2" />
                                </span>
                              </Button>
                            </>
                          )}
                          {showHiddenSelected && hiddenSelectedRows.length > 0 && (
                            <Table size="sm" className="mb-0">
                              <tbody>
                                {hiddenSelectedRows.map((row) => (
                                  <RowCheckbox
                                    key={row.productId}
                                    row={row}
                                    checked
                                    onToggle={() => toggleRow(row)}
                                  />
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      )}
                      {rows.length === 0 ? (
                        <p className="mb-0">No other tours with an editable bid were found.</p>
                      ) : (
                        <>
                          <Table size="sm" className="mb-0">
                            <tbody>
                              {rows.map((row, index) => (
                                <React.Fragment key={row.productId}>
                                  {(index === 0 || rows[index - 1].group !== row.group) && (
                                    <tr>
                                      <td colSpan={2} className="border-0 fw-bold fst-italic pt-2">
                                        {row.group}:
                                      </td>
                                      <td className="border-0 text-end pt-2">
                                        {groupCleanRows(rows, row.group).length > 0 && (
                                          <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 border-0 align-baseline text-primary text-decoration-underline"
                                            onClick={() => toggleGroup(row.group)}
                                          >
                                            {groupAllSelected(row.group)
                                              ? `Clear (${groupCleanRows(rows, row.group).length})`
                                              : `Select all — no conflicts (${
                                                  groupCleanRows(rows, row.group).length
                                                })`}
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                  <RowCheckbox
                                    row={row}
                                    checked={selectedRows.has(row.productId)}
                                    onToggle={() => toggleRow(row)}
                                  />
                                </React.Fragment>
                              ))}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </ErrorBoundary>
      </Modal.Body>
      <Modal.Footer>
        {run ? (
          <Button variant="outline-secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="primary"
              size="sm"
              disabled={loading || submitting || selectedRows.size === 0 || fieldNames.length === 0}
              onClick={submit}
            >
              Apply to {selectedRows.size} tour{selectedRows.size === 1 ? "" : "s"}
            </Button>
            <Button variant="outline-danger" size="sm" onClick={onHide} disabled={submitting}>
              Cancel
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ApplyToToursModal
